import { Loading } from 'ui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import TutorAITabs from '../../components/atoms/TutorAITabs/TutorAITabs';
import AnalyticsFilterBar from '../../components/compositions/AnalyticsFilterBar/AnalyticsFilterBar';
import TeacherLayoutWrapper from '../../components/layouts/TeacherLayoutWrapper/TeacherLayoutWrapper';
import TeacherPageHeader from '../../components/layouts/TeacherPageHeader/TeacherPageHeader';
import { useAnalyticsFilters } from '../../contexts/AnalyticsFiltersContext';
import './_teacher-ai-analytics-panel.scss';
import IntentDistributionSection from './sections/IntentDistributionSection/IntentDistributionSection';
import TopicRankingSection from './sections/TopicRankingSection/TopicRankingSection';
import TutorUsageSection from './sections/TutorUsageSection/TutorUsageSection';
import type { AnalyticsTabInitialTrigger } from './useAnalyticsTabOpenedEvent';
import useTeacherAnalyticsOpenedEvent from './useTeacherAnalyticsOpenedEvent';

/**
 * TeacherAIAnalyticsPanelView - Main view for the AI analytics panel.
 * Displays class and time period filters with an empty content area for future phases.
 */
function TeacherAIAnalyticsPanelView(): JSX.Element {
  const { t } = useTranslation(['chatbot']);
  const { allCourseIds, groupCourseMap, loading, selectedClass, selectedTimePeriod } = useAnalyticsFilters();
  const [activeTab, setActiveTab] = useState<string>('tutor-use');
  const [tabOpenTrigger, setTabOpenTrigger] = useState<AnalyticsTabInitialTrigger>('default');
  useTeacherAnalyticsOpenedEvent();

  function handleTabChange(tabId: string): void {
    setTabOpenTrigger('tab_click');
    setActiveTab(tabId);
  }

  return (
    <TeacherLayoutWrapper
      activeNavigationId="analytics"
      contentGap="xs"
      gutter="standard"
      header={
        <TeacherPageHeader
          actions={<AnalyticsFilterBar />}
          subtitle={t('chatbot:ai_analytics_panel_subtitle')}
          title={t('chatbot:ai_analytics_panel_title')}
          variant="title"
        />
      }
      showBackArrowWhenCollapsed={true}
      sidebarMode="expanded"
      toolbar={
        <TutorAITabs
          tabs={[
            { id: 'tutor-use', label: t('chatbot:ai_analytics_tab_tutor_use') },
            { id: 'topic-ranking', label: t('chatbot:ai_analytics_tab_queries') },
            { id: 'intent-distribution', label: t('chatbot:ai_analytics_tab_performance') },
          ]}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <div className="teacher-ai-analytics-panel">
          <div className="teacher-ai-analytics-panel__content">
            {activeTab === 'tutor-use' && (
              <TutorUsageSection
                allCourseIds={allCourseIds}
                classValue={selectedClass.value}
                groupCourseMap={groupCourseMap}
                tabOpenTrigger={tabOpenTrigger}
                timePeriodValue={selectedTimePeriod.value}
              />
            )}
            {activeTab === 'topic-ranking' && (
              <TopicRankingSection
                allCourseIds={allCourseIds}
                classValue={selectedClass.value}
                groupCourseMap={groupCourseMap}
                tabOpenTrigger={tabOpenTrigger}
                timePeriodValue={selectedTimePeriod.value}
              />
            )}
            {activeTab === 'intent-distribution' && (
              <IntentDistributionSection
                allCourseIds={allCourseIds}
                classValue={selectedClass.value}
                groupCourseMap={groupCourseMap}
                tabOpenTrigger={tabOpenTrigger}
                timePeriodValue={selectedTimePeriod.value}
              />
            )}
          </div>
        </div>
      )}
    </TeacherLayoutWrapper>
  );
}

export default TeacherAIAnalyticsPanelView;
